import React, { useState, useEffect } from 'react';
import apiClient, { getApiUrl } from '../../utils/apiClient';
import axios from 'axios';import PageContainer from '../layout/PageContainer';


interface Reservation {
  id: number;
  customer_name: string;
  customer_phone: string;
  pickup_time: string;
  items: Array<{
    id: string;
    name: string;
    quantity: number;
    price: number;
  }>;
  total_amount: number;
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'completed' | 'cancelled';
  notes: string;
  created_at: string;
}

const ReservationManagement: React.FC = () => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [todayReservations, setTodayReservations] = useState<Reservation[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [newReservation, setNewReservation] = useState({
    customer_name: '',
    customer_phone: '',
    pickup_date: '',
    pickup_time: '',
    notes: '',
    items: [] as Array<{ id: string; name: string; quantity: number; price: number }>
  });
  const [showNewReservationModal, setShowNewReservationModal] = useState(false);
  const [menus, setMenus] = useState<Array<{ id: string; name: string; price: number; category: string }>>([]);

  useEffect(() => {
    fetchReservations();
    fetchTodayReservations();
    fetchMenus();
  }, []);

  const fetchReservations = async () => {
    try {
      const response = await apiClient.get('/reservations?upcoming=true');
      setReservations(response.data);
    } catch (error) {
      console.error('予約データ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodayReservations = async () => {
    try {
      const response = await apiClient.get('/reservations/today');
      setTodayReservations(response.data);
    } catch (error) {
      console.error('本日の予約データ取得エラー:', error);
    }
  };

  const fetchMenus = async () => {
    try {
      const response = await apiClient.get('/menus');
      setMenus(response.data);
    } catch (error) {
      console.error('メニューデータ取得エラー:', error);
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await apiClient.put('/reservations/${id}/status', {
        status: newStatus
      });
      await fetchReservations();
      await fetchTodayReservations();
      alert('ステータスを更新しました');
    } catch (error) {
      console.error('ステータス更新エラー:', error);
      alert('ステータスの更新に失敗しました');
    }
  };

  const handleCreateReservation = async () => {
    if (!newReservation.customer_name || !newReservation.customer_phone || 
        !newReservation.pickup_date || !newReservation.pickup_time || 
        newReservation.items.length === 0) {
      alert('必須項目を入力してください');
      return;
    }

    const pickupDateTime = `${newReservation.pickup_date} ${newReservation.pickup_time}:00`;
    const totalAmount = newReservation.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    try {
      await apiClient.post('/reservations', {
        customer_name: newReservation.customer_name,
        customer_phone: newReservation.customer_phone,
        pickup_time: pickupDateTime,
        items: newReservation.items,
        total_amount: totalAmount,
        notes: newReservation.notes
      });
      
      setShowNewReservationModal(false);
      setNewReservation({
        customer_name: '',
        customer_phone: '',
        pickup_date: '',
        pickup_time: '',
        notes: '',
        items: []
      });
      
      await fetchReservations();
      await fetchTodayReservations();
      alert('予約を登録しました');
    } catch (error) {
      console.error('予約作成エラー:', error);
      alert('予約の登録に失敗しました');
    }
  };

  const addItemToReservation = (menu: any) => {
    const existingItem = newReservation.items.find(item => item.id === menu.id);
    if (existingItem) {
      setNewReservation({
        ...newReservation,
        items: newReservation.items.map(item =>
          item.id === menu.id ? { ...item, quantity: item.quantity + 1 } : item
        )
      });
    } else {
      setNewReservation({
        ...newReservation,
        items: [...newReservation.items, { 
          id: menu.id, 
          name: menu.name, 
          quantity: 1, 
          price: menu.price 
        }]
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { color: string; bg: string; label: string } } = {
      pending: { color: 'text-gray-600', bg: 'bg-gray-100', label: '未確認' },
      confirmed: { color: 'text-blue-600', bg: 'bg-blue-100', label: '確認済' },
      preparing: { color: 'text-yellow-600', bg: 'bg-yellow-100', label: '調理中' },
      ready: { color: 'text-green-600', bg: 'bg-green-100', label: '準備完了' },
      completed: { color: 'text-gray-600', bg: 'bg-gray-100', label: '完了' },
      cancelled: { color: 'text-red-600', bg: 'bg-red-100', label: 'キャンセル' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${config.bg} ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return date.toLocaleString('ja-JP', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredReservations = selectedStatus === 'all' 
    ? reservations 
    : reservations.filter(r => r.status === selectedStatus);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">予約管理</h1>
          <p className="text-gray-600 mt-2">テイクアウト予約の管理と状況確認</p>
        </div>
        <button
          onClick={() => setShowNewReservationModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          新規予約
        </button>
      </div>

      {/* 本日の予約サマリー */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">本日の予約</div>
          <div className="text-2xl font-bold">{todayReservations.length}件</div>
        </div>
        <div className="bg-yellow-50 rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">調理中</div>
          <div className="text-2xl font-bold">
            {todayReservations.filter(r => r.status === 'preparing').length}件
          </div>
        </div>
        <div className="bg-green-50 rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">準備完了</div>
          <div className="text-2xl font-bold">
            {todayReservations.filter(r => r.status === 'ready').length}件
          </div>
        </div>
        <div className="bg-blue-50 rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">未確認</div>
          <div className="text-2xl font-bold">
            {todayReservations.filter(r => r.status === 'pending').length}件
          </div>
        </div>
      </div>

      {/* ステータスフィルター */}
      <div className="mb-6 flex space-x-2">
        {['all', 'pending', 'confirmed', 'preparing', 'ready', 'completed', 'cancelled'].map(status => (
          <button
            key={status}
            onClick={() => setSelectedStatus(status)}
            className={`px-4 py-2 rounded-md transition-colors ${
              selectedStatus === status
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {status === 'all' ? '全て' : getStatusBadge(status).props.children}
          </button>
        ))}
      </div>

      {/* 予約リスト */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                受取時間
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                お客様
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                商品
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                金額
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ステータス
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredReservations.map(reservation => (
              <tr key={reservation.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {formatDateTime(reservation.pickup_time)}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">{reservation.customer_name}</div>
                  <div className="text-sm text-gray-500">{reservation.customer_phone}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">
                    {reservation.items.map(item => (
                      <div key={item.id}>
                        {item.name} × {item.quantity}
                      </div>
                    ))}
                  </div>
                  {reservation.notes && (
                    <div className="text-xs text-gray-500 mt-1">備考: {reservation.notes}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    ¥{reservation.total_amount.toLocaleString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(reservation.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={reservation.status}
                    onChange={(e) => handleStatusChange(reservation.id, e.target.value)}
                    className="text-sm border rounded px-2 py-1"
                  >
                    <option value="pending">未確認</option>
                    <option value="confirmed">確認済</option>
                    <option value="preparing">調理中</option>
                    <option value="ready">準備完了</option>
                    <option value="completed">完了</option>
                    <option value="cancelled">キャンセル</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 新規予約モーダル */}
      {showNewReservationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">新規予約登録</h2>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-2">お客様名</label>
                <input
                  type="text"
                  value={newReservation.customer_name}
                  onChange={(e) => setNewReservation({ ...newReservation, customer_name: e.target.value })}
                  className="w-full p-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">電話番号</label>
                <input
                  type="tel"
                  value={newReservation.customer_phone}
                  onChange={(e) => setNewReservation({ ...newReservation, customer_phone: e.target.value })}
                  className="w-full p-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">受取日</label>
                <input
                  type="date"
                  value={newReservation.pickup_date}
                  onChange={(e) => setNewReservation({ ...newReservation, pickup_date: e.target.value })}
                  className="w-full p-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">受取時間</label>
                <input
                  type="time"
                  value={newReservation.pickup_time}
                  onChange={(e) => setNewReservation({ ...newReservation, pickup_time: e.target.value })}
                  className="w-full p-2 border rounded-md"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">備考</label>
              <textarea
                value={newReservation.notes}
                onChange={(e) => setNewReservation({ ...newReservation, notes: e.target.value })}
                className="w-full p-2 border rounded-md"
                rows={2}
              />
            </div>
            
            <div className="mb-4">
              <h3 className="font-medium mb-2">商品選択</h3>
              <div className="grid grid-cols-2 gap-2 mb-4 max-h-40 overflow-y-auto">
                {menus.map(menu => (
                  <button
                    key={menu.id}
                    onClick={() => addItemToReservation(menu)}
                    className="text-left p-2 border rounded hover:bg-gray-100"
                  >
                    <div className="text-sm font-medium">{menu.name}</div>
                    <div className="text-xs text-gray-500">¥{menu.price}</div>
                  </button>
                ))}
              </div>
              
              {newReservation.items.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">選択済み商品</h4>
                  <div className="space-y-2">
                    {newReservation.items.map(item => (
                      <div key={item.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <span>{item.name}</span>
                        <div className="flex items-center space-x-2">
                          <span>×{item.quantity}</span>
                          <span>¥{item.price * item.quantity}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 pt-2 border-t font-medium text-right">
                    合計: ¥{newReservation.items.reduce((sum, item) => sum + (item.price * item.quantity), 0).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNewReservationModal(false);
                  setNewReservation({
                    customer_name: '',
                    customer_phone: '',
                    pickup_date: '',
                    pickup_time: '',
                    notes: '',
                    items: []
                  });
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                キャンセル
              </button>
              <button
                onClick={handleCreateReservation}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                登録
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationManagement;